import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import RecordNotFound from "../../core/components/RecordNotFound";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import TemplatePreviewModal from "./templatePreviewModal";
import {
  FileText,
  Save,
  ArrowLeft,
  Code2,
  Paintbrush,
  Search,
  Copy,
  ShieldAlert,
  Eye,
  Trash2,
} from "lucide-react";

const TEMPLATE_TYPE_GROUPS = [
  {
    label: "Sales & Receipts",
    types: [
      { value: "sales_invoice", label: "Sales Invoice" },
      { value: "sales_receipt", label: "Sales Receipt" },
      { value: "quotation", label: "Quotation" },
    ],
  },
  {
    label: "Repairs & Service",
    types: [
      { value: "repair_job_note", label: "Repair Job Note" },
      { value: "repair_job_label", label: "Repair Job Label" },
      { value: "repair_invoice", label: "Repair Invoice" },
    ],
  },
  {
    label: "Barcodes & Labels",
    types: [
      { value: "product_barcode", label: "Product Barcode" },
      { value: "serial_barcode", label: "Serial Barcode" },
    ],
  },
  {
    label: "Purchasing & Inventory",
    types: [
      { value: "purchase_order", label: "Purchase Order" },
      { value: "goods_received_note", label: "Goods Received Note" },
      { value: "stock_transfer_document", label: "Stock Transfer Document" },
    ],
  },
  {
    label: "Reports",
    types: [
      { value: "sales_summary_report", label: "Sales Summary Report" },
      { value: "sales_invoice_report", label: "Sales Invoice Report" },
      { value: "customerwise_sales_report", label: "Customerwise Sales Report" },
      { value: "product_stock_report", label: "Product Stock Report" },
      { value: "inventory_movement_report", label: "Inventory Movement Report" },
      { value: "stock_transfer_report", label: "Stock Transfer Report" },
      { value: "parts_usage_report", label: "Parts Usage Report" },
      { value: "daily_cash_balance_report", label: "Daily Cash Balance Report" },
      { value: "expense_report", label: "Expense Report" },
      { value: "supplierwise_expense_report", label: "Supplierwise Expense Report" },
      { value: "profit_loss_report", label: "Profit & Loss Report" },
    ],
  },
  {
    label: "Email Notifications",
    types: [
      { value: "email_header", label: "Email Header" },
      { value: "email_footer", label: "Email Footer" },
      { value: "email_customer_registration", label: "Email: Customer Registration" },
      { value: "email_low_stock_alert", label: "Email: Low Stock Alert" },
      { value: "email_repair_status_update", label: "Email: Repair Status Update" },
      { value: "email_task_reminder", label: "Email: Task Reminder" },
      { value: "email_new_admin_account", label: "Email: New Admin Account" },
      { value: "email_sale_invoice", label: "Email: Sale Invoice" },
      { value: "email_password_reset_otp", label: "Email: Password Reset OTP" },
    ],
  },
];

export default function EditTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contentRef = useRef(null);

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permissions) return false;
    if (Array.isArray(permission)) return permission.some((p) => permissions.includes(p));
    return permissions.includes(permission);
  };

  const canReadTemplates = hasPermission("template read");
  const canCreateTemplates = hasPermission("template create");
  const canUpdateTemplates = hasPermission("template update");
  const canDeleteTemplates = hasPermission("template delete");
  const canAccess = canReadTemplates || canUpdateTemplates;

  useEffect(() => {
    if (!canAccess) {
      navigate(all_routes.templates);
    }
  }, [canAccess, navigate]);

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeCodeTab, setActiveCodeTab] = useState("html");
  const [placeholderSearch, setPlaceholderSearch] = useState("");

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
  const [configOptions, setConfigOptions] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch existing template
  const { data: template, isLoading } = useQuery({
    queryKey: ["template", id],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.TEMPLATES.TEMPLATE_DETAILS(id)
      );
      return res.data?.data ?? res.data;
    },
    enabled: Boolean(id && canAccess),
  });

  const isSystem = Boolean(template?.is_system);
  const isReadOnly = isSystem || !canUpdateTemplates;

  useEffect(() => {
    if (template) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        name: template.name ?? "",
        type: template.type ?? "",
        content: template.content ?? "",
        styles: template.styles ?? "",
        store_id: template.store_id ?? "",
        is_active: template.is_active ?? true,
      });
    }
  }, [template]);

  // Fetch placeholders for selected type
  const { data: placeholders = {}, isLoading: placeholdersLoading } = useQuery({
    queryKey: ["placeholders", form?.type],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.PLACEHOLDERS, {
        params: { type: form.type },
      });
      return res.data?.placeholders || res.data?.data || res.data || {};
    },
    enabled: Boolean(form?.type && canAccess),
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (!canUpdateTemplates) {
        throw new Error("You do not have permission to update templates.");
      }
      const payload = {
        name: data.name,
        type: data.type,
        content: data.content,
        is_active: data.is_active,
        styles: data.styles || null,
        store_id: data.store_id || null,
      };
      const res = await apiClient.put(
        API_ENDPOINTS.TEMPLATES.TEMPLATE_DETAILS(id),
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      queryClient.invalidateQueries({ queryKey: ["template", id] });
      toast.success("Template updated successfully");
      navigate(all_routes.templates);
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message || "Failed to save template";
      toast.error(msg);
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!canDeleteTemplates) {
        throw new Error("You do not have permission to delete templates.");
      }
      await apiClient.delete(API_ENDPOINTS.TEMPLATES.TEMPLATE_DETAILS(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Template deleted successfully");
      navigate(all_routes.templates);
    },
    onError: (err) => {
      const msg = err.response?.data?.message || err.message || "Failed to delete template";
      toast.error(msg);
    },
  });

  const handleDelete = () => {
    if (!canDeleteTemplates) {
      toast.error("You do not have permission to delete templates.");
      return;
    }
    if (isSystem) {
      toast.error("System templates cannot be deleted.");
      return;
    }
    deleteMutation.mutate();
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const insertPlaceholder = (key, options = null) => {
    if (isReadOnly) return;
    let placeholder = `##${key}`;
    if (options && Object.keys(options).length > 0) {
      const finalOptions = { ...options };
      if (selectedPlaceholder?.types?.includes("string")) {
        if (!finalOptions.type) finalOptions.type = "text";
        if (finalOptions.type === "barcode" && !finalOptions.code_type) {
          finalOptions.code_type = "C128";
        }
      }
      if (selectedPlaceholder?.types?.includes("image") && !finalOptions.type) {
        finalOptions.type = "image";
      }
      if (
        selectedPlaceholder?.types?.includes("barcode") &&
        !selectedPlaceholder?.types?.includes("string") &&
        !finalOptions.type
      ) {
        finalOptions.type = "barcode";
      }

      const optString = Object.entries(finalOptions)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([k, v]) => {
          if (typeof v === "object") {
            return `${k}='${JSON.stringify(v)}'`;
          }
          const attrKey = k === "precision" ? "decimal" : k;
          return `${attrKey}="${v}"`;
        })
        .join(" ");
      if (optString) {
        placeholder += ` [${optString}]`;
      }
    }
    placeholder += "##";

    const textarea = contentRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const scrollTop = textarea.scrollTop;
      const text = form.content || "";
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);

      setForm((prev) => ({
        ...prev,
        content: before + placeholder + after,
      }));

      setTimeout(() => {
        textarea.focus();
        const newPos = start + placeholder.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.scrollTop = scrollTop;
      }, 0);
    } else {
      setForm((prev) => ({
        ...prev,
        content: (prev.content || "") + placeholder,
      }));
    }
    setShowConfigModal(false);
  };

  const handlePlaceholderClick = (key) => {
    const info = placeholders[key];
    if (!info) {
      insertPlaceholder(key);
      return;
    }

    const types = info.types || (info.type ? [info.type] : []);
    const hasOptions = info.options && Object.keys(info.options).length > 0;
    const hasMultipleTypes = types.length > 1;
    const isDateWithFormats =
      types.includes("date") && info.options?.formats?.length > 0;
    const isDateWithDefaultFormat =
      types.includes("date") &&
      !!info.options?.format &&
      !isDateWithFormats &&
      !hasMultipleTypes;
    const isImage = types.includes("image");
    const isTable = types.includes("table");
    const isCurrencyOrNumber =
      types.includes("currency") || types.includes("number");
    const isStandaloneBarcode =
      types.includes("barcode") && !types.includes("string");

    const shouldSkipModal =
      !hasOptions &&
      !hasMultipleTypes &&
      !isDateWithFormats &&
      !isImage &&
      !isTable &&
      !isCurrencyOrNumber &&
      !isStandaloneBarcode;

    if (isDateWithDefaultFormat) {
      insertPlaceholder(key, { format: info.options.format });
      return;
    }

    if (shouldSkipModal) {
      const implicitOptions = {};
      if (types.includes("currency")) {
        if (info.options?.prefix) implicitOptions.prefix = info.options.prefix;
        if (info.options?.precision !== undefined)
          implicitOptions.precision = info.options.precision;
      }
      insertPlaceholder(key, implicitOptions);
    } else {
      setSelectedPlaceholder({ key, ...info, types });
      let options = { ...(info.options || {}) };

      if (
        types.includes("date") &&
        info.options?.formats?.length > 0 &&
        !options.format
      ) {
        options.format = info.options.formats[0];
      }

      if (types.includes("image")) {
        if (!options.width) options.width = "200px";
        if (!options.height) options.height = "auto";
      }

      if (isStandaloneBarcode) {
        if (!options.code_type) options.code_type = "C128";
        if (!options.width) options.width = "2";
        if (!options.height) options.height = "35";
      }

      if (types.includes("string") && !options.type) {
        options.type = "text";
      }

      if (types.includes("table") && info.options?.columns) {
        options.columns = JSON.parse(JSON.stringify(info.options.columns));
      }

      setConfigOptions(options);
      setShowConfigModal(true);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.type) e.type = "Type is required";
    if (!form.content.trim()) e.content = "Content is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleClone = () => {
    if (!canCreateTemplates) {
      toast.error("You do not have permission to create templates.");
      return;
    }
    const cloneName = form.name?.endsWith("(Copy)")
      ? form.name
      : `${form.name || template?.name || "Template"} (Copy)`;
    navigate(all_routes.addTemplate, {
      state: {
        cloneTemplate: {
          name: cloneName,
          type: form.type || template?.type,
          content: form.content ?? template?.content ?? "",
          styles: form.styles ?? template?.styles ?? "",
          store_id: form.store_id || null,
          is_active: true,
        },
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canUpdateTemplates) {
      toast.error("You do not have permission to update templates.");
      return;
    }
    if (isSystem) {
      toast.error(
        "System templates cannot be edited directly. Please click 'Clone as New Template' to save your customizations."
      );
      return;
    }
    if (!validate()) return;
    mutation.mutate(form);
  };

  if (!canAccess) {
    return <RecordNotFound />;
  }

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="flex justify-center items-center py-20">
          <Spinner />
        </div>
      </PageContainer>
    );
  }

  const filteredPlaceholders = Object.entries(placeholders).filter(
    ([key, info]) => {
      const q = placeholderSearch.toLowerCase();
      return (
        key.toLowerCase().includes(q) ||
        (info.label && info.label.toLowerCase().includes(q))
      );
    }
  );

  return (
    <PageContainer>
      <PageHeader>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Link
              to={all_routes.templates}
              className="hover:text-primary transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" /> Templates
            </Link>
            <span>/</span>
            <span>
              {isSystem
                ? "View System Template"
                : canUpdateTemplates
                  ? "Edit Template"
                  : "View Template"}
            </span>
          </div>
          <PageHeaderHeading className="flex items-center gap-2 flex-wrap">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <span>{form.name || "Edit Template"}</span>
            {isSystem ? (
              <Badge
                variant="outline"
                className="text-xs text-muted-foreground font-normal bg-muted/60 px-2 py-0.5 ml-1"
              >
                System (Protected)
              </Badge>
            ) : !canUpdateTemplates ? (
              <Badge
                variant="outline"
                className="text-xs text-amber-600 bg-amber-500/10 border-amber-500/20 font-normal px-2 py-0.5 ml-1"
              >
                View Only
              </Badge>
            ) : null}
          </PageHeaderHeading>
        </div>
        <PageHeaderActions>
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="gap-2 cursor-pointer"
          >
            <Eye className="h-4 w-4" />
            Preview
          </Button>
          {!isSystem && canDeleteTemplates && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirm(true)}
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/20 cursor-pointer"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(all_routes.templates)}
            className="cursor-pointer"
          >
            {isReadOnly ? "Back" : "Cancel"}
          </Button>
          {isSystem ? (
            canCreateTemplates && (
              <Button
                type="button"
                onClick={handleClone}
                className="gap-2 cursor-pointer"
              >
                <Copy className="h-4 w-4" />
                Clone as New Template
              </Button>
            )
          ) : canUpdateTemplates ? (
            <Button
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="gap-2 cursor-pointer"
            >
              <Save className="h-4 w-4" />
              {mutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          ) : canCreateTemplates ? (
            <Button
              type="button"
              onClick={handleClone}
              className="gap-2 cursor-pointer"
            >
              <Copy className="h-4 w-4" />
              Clone as New Template
            </Button>
          ) : null}
        </PageHeaderActions>
      </PageHeader>

      {/* System Template Protection Banner */}
      {isSystem && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3.5 border rounded-lg bg-muted/30 text-xs mb-6">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="h-4.5 w-4.5 text-muted-foreground shrink-0" />
            <div>
              <span className="font-semibold text-foreground">
                System Template (Edit Protected)
              </span>
              <p className="text-muted-foreground mt-0.5">
                Default system templates cannot be edited directly. Clone this template to create a new editable template with your own customizations.
              </p>
            </div>
          </div>
          {canCreateTemplates && (
            <Button
              type="button"
              size="sm"
              onClick={handleClone}
              className="gap-1.5 shrink-0 cursor-pointer"
            >
              <Copy className="h-3.5 w-3.5" />
              Clone as New Template
            </Button>
          )}
        </div>
      )}

      {/* Read-Only Permission Banner */}
      {!isSystem && !canUpdateTemplates && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3.5 border rounded-lg bg-amber-500/10 border-amber-500/20 text-xs mb-6 text-amber-900 dark:text-amber-200">
          <div className="flex items-center gap-2.5">
            <ShieldAlert className="h-4.5 w-4.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <span className="font-semibold">
                Read-Only Mode
              </span>
              <p className="text-muted-foreground mt-0.5">
                You have view permissions for this template. You do not have permission to edit or save changes.
              </p>
            </div>
          </div>
          {canCreateTemplates && (
            <Button
              type="button"
              size="sm"
              onClick={handleClone}
              className="gap-1.5 shrink-0 cursor-pointer"
            >
              <Copy className="h-3.5 w-3.5" />
              Clone as New Template
            </Button>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Template Information & Placeholders */}
          <div className="space-y-6 lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">
                  Template Information
                </CardTitle>
                <CardDescription className="text-xs">
                  Basic properties and classification
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="template-name">
                    Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    placeholder="e.g. Standard Sales Receipt"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    maxLength={255}
                    disabled={isReadOnly}
                  />
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="template-type">
                    Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => handleChange("type", v)}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger id="template-type">
                      <SelectValue placeholder="Select template type..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {TEMPLATE_TYPE_GROUPS.map((group) => (
                        <SelectGroup key={group.label}>
                          <SelectLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            {group.label}
                          </SelectLabel>
                          {group.types.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.type && (
                    <p className="text-xs text-destructive">{errors.type}</p>
                  )}
                </div>

                <div className="flex items-center justify-between rounded-lg border p-2.5 bg-muted/20">
                  <div className="space-y-0.5">
                    <Label htmlFor="template-active" className="cursor-pointer text-xs font-medium">
                      Active Status
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      {isSystem
                        ? "System default templates are always active"
                        : "Enable template for system output"}
                    </p>
                  </div>
                  <Switch
                    id="template-active"
                    checked={form.is_active}
                    disabled={isReadOnly}
                    onCheckedChange={(v) => handleChange("is_active", v)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">
                  Available Placeholders
                </CardTitle>
                <CardDescription className="text-xs">
                  Click any tag to insert into template content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search tags..."
                    value={placeholderSearch}
                    onChange={(e) => setPlaceholderSearch(e.target.value)}
                    className="pl-8 h-8 text-xs"
                  />
                </div>

                <div className="max-h-[420px] overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
                  {placeholdersLoading ? (
                    <div className="py-6 flex justify-center text-xs text-muted-foreground">
                      Loading placeholders...
                    </div>
                  ) : filteredPlaceholders.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground text-xs italic">
                      {form.type ? "No placeholders available for this type." : "Select a template type to view placeholders."}
                    </div>
                  ) : (
                    filteredPlaceholders.map(([key, info]) => (
                      <div
                        key={key}
                        className={`p-2 rounded-md border border-border/70 transition-colors ${isReadOnly
                          ? "opacity-80 cursor-default"
                          : "hover:border-border hover:bg-muted/40 cursor-pointer group"
                          }`}
                        onClick={() => !isReadOnly && handlePlaceholderClick(key)}
                      >
                        <div className="flex justify-between items-center gap-1.5">
                          <span
                            className={`text-xs font-medium truncate ${isReadOnly
                              ? "text-foreground"
                              : "text-foreground group-hover:text-primary transition-colors"
                              }`}
                          >
                            {info.label || key}
                          </span>
                          <code className="text-[10px] px-1 py-0.5 rounded bg-muted font-mono text-muted-foreground shrink-0">
                            ##{key}##
                          </code>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Code Editor */}
          <div className="space-y-6 lg:col-span-2">
            <Card className="h-full flex flex-col">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Code2 className="h-4 w-4 text-muted-foreground" />
                    <CardTitle className="text-sm font-semibold">
                      Template Code
                    </CardTitle>
                  </div>

                  <Tabs
                    value={activeCodeTab}
                    onValueChange={setActiveCodeTab}
                    className="w-auto"
                  >
                    <TabsList className="h-7 bg-muted/60 p-0.5">
                      <TabsTrigger value="html" className="h-6 text-xs px-2.5 gap-1.5">
                        <Code2 className="h-3 w-3" />
                        HTML
                      </TabsTrigger>
                      <TabsTrigger value="css" className="h-6 text-xs px-2.5 gap-1.5">
                        <Paintbrush className="h-3 w-3" />
                        CSS
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </CardHeader>

              <CardContent className="p-0 flex-1 flex flex-col min-h-[500px]">
                {/* Quick Insert Toolbar */}
                {activeCodeTab === "html" && Object.keys(placeholders).length > 0 && (
                  <div className="bg-muted/30 px-3 py-1.5 border-b flex items-center gap-1.5 overflow-x-auto">
                    <span className="text-[11px] font-medium text-muted-foreground whitespace-nowrap">
                      Insert:
                    </span>
                    {Object.entries(placeholders)
                      .slice(0, 6)
                      .map(([key, info]) => (
                        <Badge
                          key={key}
                          variant="outline"
                          className={`text-[10px] px-1.5 py-0 h-5 font-normal text-muted-foreground whitespace-nowrap ${isReadOnly
                            ? "opacity-70 cursor-default"
                            : "cursor-pointer hover:bg-muted"
                            }`}
                          onClick={() => !isReadOnly && handlePlaceholderClick(key)}
                        >
                          {info.label || key}
                        </Badge>
                      ))}
                  </div>
                )}

                {/* HTML Textarea */}
                {activeCodeTab === "html" && (
                  <div className="flex-1 p-3 flex flex-col">
                    <Textarea
                      ref={contentRef}
                      id="template-content"
                      placeholder="Enter HTML template markup with ##tags##..."
                      value={form.content}
                      onChange={(e) => handleChange("content", e.target.value)}
                      readOnly={isReadOnly}
                      className="flex-1 min-h-[460px] font-mono text-xs leading-relaxed border-0 focus-visible:ring-0 p-2 shadow-none resize-none"
                    />
                    {errors.content && (
                      <p className="text-xs text-destructive mt-1">{errors.content}</p>
                    )}
                  </div>
                )}

                {/* CSS Textarea */}
                {activeCodeTab === "css" && (
                  <div className="flex-1 p-3 flex flex-col">
                    <Textarea
                      id="template-styles"
                      placeholder="body { font-family: sans-serif; } .receipt-title { font-weight: bold; }"
                      value={form.styles}
                      onChange={(e) => handleChange("styles", e.target.value)}
                      readOnly={isReadOnly}
                      className="flex-1 min-h-[460px] font-mono text-xs leading-relaxed border-0 focus-visible:ring-0 p-2 shadow-none resize-none"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Placeholder Configuration Dialog */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure {selectedPlaceholder?.label}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {selectedPlaceholder?.types?.includes("string") && (
              <div className="space-y-2">
                <Label>Display Type</Label>
                <Select
                  value={configOptions.type || "text"}
                  onValueChange={(val) => {
                    const newOptions = { ...configOptions, type: val };
                    if (val === "barcode" && !newOptions.code_type)
                      newOptions.code_type = "C128";
                    setConfigOptions(newOptions);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Plain Text</SelectItem>
                    {selectedPlaceholder.types.includes("barcode") && (
                      <SelectItem value="barcode">Barcode</SelectItem>
                    )}
                    {selectedPlaceholder.types.includes("qr") && (
                      <SelectItem value="qr">QR Code</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedPlaceholder?.types?.includes("date") &&
              selectedPlaceholder?.options?.formats?.length > 0 && (
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select
                    value={
                      configOptions.format ||
                      selectedPlaceholder.options.formats[0]
                    }
                    onValueChange={(val) =>
                      setConfigOptions({ ...configOptions, format: val })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedPlaceholder.options.formats.map((fmt) => (
                        <SelectItem key={fmt} value={fmt}>
                          {fmt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

            {selectedPlaceholder?.types?.includes("image") && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Width</Label>
                  <Input
                    value={configOptions.width || ""}
                    onChange={(e) =>
                      setConfigOptions({
                        ...configOptions,
                        width: e.target.value,
                      })
                    }
                    placeholder="e.g. 200px"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <Input
                    value={configOptions.height || ""}
                    onChange={(e) =>
                      setConfigOptions({
                        ...configOptions,
                        height: e.target.value,
                      })
                    }
                    placeholder="e.g. auto"
                  />
                </div>
              </div>
            )}

            {/* Standalone barcode (not a string rendered as barcode) */}
            {selectedPlaceholder?.types?.includes("barcode") &&
              !selectedPlaceholder?.types?.includes("string") && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Barcode Type</Label>
                    <Select
                      value={configOptions.code_type || "C128"}
                      onValueChange={(val) =>
                        setConfigOptions({ ...configOptions, code_type: val })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="C128">Code 128</SelectItem>
                        <SelectItem value="C39">Code 39</SelectItem>
                        <SelectItem value="EAN13">EAN-13</SelectItem>
                        <SelectItem value="QR">QR Code</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Width</Label>
                      <Input
                        value={configOptions.width || ""}
                        onChange={(e) =>
                          setConfigOptions({
                            ...configOptions,
                            width: e.target.value,
                          })
                        }
                        placeholder="e.g. 2"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Height</Label>
                      <Input
                        value={configOptions.height || ""}
                        onChange={(e) =>
                          setConfigOptions({
                            ...configOptions,
                            height: e.target.value,
                          })
                        }
                        placeholder="e.g. 35"
                      />
                    </div>
                  </div>
                </div>
              )}

            {selectedPlaceholder?.types?.includes("table") && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Table CSS Class</Label>
                  <Input
                    value={configOptions.table_class || ""}
                    onChange={(e) =>
                      setConfigOptions({
                        ...configOptions,
                        table_class: e.target.value,
                      })
                    }
                    placeholder="e.g. table table-striped"
                  />
                </div>

                {configOptions.columns && (
                  <div className="space-y-2">
                    <Label>Columns Configuration</Label>
                    <div className="border rounded-md divide-y max-h-60 overflow-y-auto bg-card">
                      {configOptions.columns.map((col, idx) => (
                        <div
                          key={col.key || idx}
                          className="p-2 flex flex-col gap-2 text-sm border-b last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={!col.hidden} // Assuming missing 'hidden' means visible
                              onCheckedChange={(checked) => {
                                const newCols = [...configOptions.columns];
                                newCols[idx] = {
                                  ...newCols[idx],
                                  hidden: !checked,
                                };
                                setConfigOptions({
                                  ...configOptions,
                                  columns: newCols,
                                });
                              }}
                              className="scale-75"
                            />
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <Input
                                value={col.header}
                                onChange={(e) => {
                                  const newCols = [...configOptions.columns];
                                  newCols[idx] = {
                                    ...newCols[idx],
                                    header: e.target.value,
                                  };
                                  setConfigOptions({
                                    ...configOptions,
                                    columns: newCols,
                                  });
                                }}
                                className="h-7 text-xs"
                                placeholder="Header"
                              />
                              <Select
                                value={col.align || "left"}
                                onValueChange={(val) => {
                                  const newCols = [...configOptions.columns];
                                  newCols[idx] = {
                                    ...newCols[idx],
                                    align: val,
                                  };
                                  setConfigOptions({
                                    ...configOptions,
                                    columns: newCols,
                                  });
                                }}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="left">Left</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                  <SelectItem value="right">Right</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Column Specific Options */}
                          {(col.types?.includes("currency") ||
                            col.type === "currency" ||
                            col.types?.includes("number") ||
                            col.type === "number") && (
                              <div className="flex gap-2 pl-10">
                                <div className="flex items-center gap-2">
                                  <Label className="text-[10px] text-muted-foreground w-12">
                                    Prec.
                                  </Label>
                                  <Input
                                    type="number"
                                    className="h-6 w-16 text-xs p-1"
                                    value={
                                      col.options?.precision !== undefined
                                        ? col.options.precision
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const newCols = [...configOptions.columns];
                                      const newOpts = {
                                        ...(newCols[idx].options || {}),
                                      };
                                      newOpts.precision = e.target.value;
                                      newCols[idx] = {
                                        ...newCols[idx],
                                        options: newOpts,
                                      };
                                      setConfigOptions({
                                        ...configOptions,
                                        columns: newCols,
                                      });
                                    }}
                                    placeholder="2"
                                  />
                                </div>
                                {(col.types?.includes("currency") ||
                                  col.type === "currency") && (
                                    <div className="flex items-center gap-2">
                                      <Label className="text-[10px] text-muted-foreground w-12">
                                        Sym.
                                      </Label>
                                      <Input
                                        className="h-6 w-16 text-xs p-1"
                                        value={col.options?.prefix || ""}
                                        onChange={(e) => {
                                          const newCols = [
                                            ...configOptions.columns,
                                          ];
                                          const newOpts = {
                                            ...(newCols[idx].options || {}),
                                          };
                                          newOpts.prefix = e.target.value;
                                          newCols[idx] = {
                                            ...newCols[idx],
                                            options: newOpts,
                                          };
                                          setConfigOptions({
                                            ...configOptions,
                                            columns: newCols,
                                          });
                                        }}
                                        placeholder="$"
                                      />
                                    </div>
                                  )}
                              </div>
                            )}

                          {(col.types?.includes("date") ||
                            col.type === "date") &&
                            (col.options?.formats?.length > 0 ||
                              col.options?.format) && (
                              <div className="flex gap-2 pl-10 items-center">
                                <Label className="text-[10px] text-muted-foreground w-12">
                                  Fmt.
                                </Label>
                                {col.options?.formats?.length > 0 ? (
                                  <Select
                                    value={
                                      col.options?.format ||
                                      col.options.formats[0]
                                    }
                                    onValueChange={(val) => {
                                      const newCols = [
                                        ...configOptions.columns,
                                      ];
                                      const newOpts = {
                                        ...(newCols[idx].options || {}),
                                      };
                                      newOpts.format = val;
                                      newCols[idx] = {
                                        ...newCols[idx],
                                        options: newOpts,
                                      };
                                      setConfigOptions({
                                        ...configOptions,
                                        columns: newCols,
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="h-6 w-40 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {col.options.formats.map((fmt) => (
                                        <SelectItem
                                          key={fmt}
                                          value={fmt}
                                          className="text-xs"
                                        >
                                          {fmt}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    className="h-6 w-40 text-xs"
                                    value={col.options?.format || ""}
                                    onChange={(e) => {
                                      const newCols = [
                                        ...configOptions.columns,
                                      ];
                                      const newOpts = {
                                        ...(newCols[idx].options || {}),
                                      };
                                      newOpts.format = e.target.value;
                                      newCols[idx] = {
                                        ...newCols[idx],
                                        options: newOpts,
                                      };
                                      setConfigOptions({
                                        ...configOptions,
                                        columns: newCols,
                                      });
                                    }}
                                    placeholder="e.g. Y-m-d"
                                  />
                                )}
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(selectedPlaceholder?.types?.includes("currency") ||
              selectedPlaceholder?.types?.includes("number")) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Precision (Decimals)</Label>
                    <Input
                      type="number"
                      value={
                        configOptions.precision !== undefined
                          ? configOptions.precision
                          : ""
                      }
                      onChange={(e) =>
                        setConfigOptions({
                          ...configOptions,
                          precision: e.target.value,
                        })
                      }
                      placeholder="e.g. 2"
                    />
                  </div>
                  {selectedPlaceholder?.types?.includes("currency") && (
                    <div className="space-y-2">
                      <Label>Prefix (Symbol)</Label>
                      <Input
                        value={configOptions.prefix || ""}
                        onChange={(e) =>
                          setConfigOptions({
                            ...configOptions,
                            prefix: e.target.value,
                          })
                        }
                        placeholder="e.g. $"
                      />
                    </div>
                  )}
                </div>
              )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                insertPlaceholder(selectedPlaceholder.key, configOptions)
              }
            >
              Insert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Live Preview Dialog */}
      <TemplatePreviewModal
        template={
          showPreview
            ? {
              id: id,
              name: form.name || template?.name || "Template Preview",
              type: form.type || template?.type,
              type_label:
                TEMPLATE_TYPE_GROUPS.flatMap((g) => g.types).find(
                  (t) => t.value === form.type
                )?.label || form.type,
              content: form.content,
              styles: form.styles,
              is_system: isSystem,
              hasDraftEdits: Boolean(
                form.content !== template?.content || form.styles !== template?.styles
              ),
            }
            : null
        }
        open={showPreview}
        onClose={() => setShowPreview(false)}
        storeId={form.store_id || template?.store_id || null}
        onDelete={!isSystem && canDeleteTemplates ? () => setShowDeleteConfirm(true) : undefined}
      />

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete template{" "}
              <strong className="text-foreground">
                "{form?.name || template?.name}"
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
